import { createPortal } from "react-dom";
import { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
  faFilter,
  faMagnifyingGlass,
  faPlus,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  CONDITION_FIELDS,
  CONDITION_TYPE_BY_FIELD,
  KIND_CONFIG,
  OPERATOR_LABEL,
  OPERATOR_OPTIONS,
  OPERATOR_READABLE,
  FIELD_LABEL,
  tagPalette,
} from "../constants";
import { conditionsToText, emptyConditionRow } from "../helpers";

export function TagChip({ tag, onRemove, onClick, active, size = "sm" }) {
  const palette = tagPalette(tag);
  const sizeClass = size === "xs" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ring-1 ring-inset select-none ${palette} ${sizeClass} ${
        onClick ? "cursor-pointer" : ""
      } ${active ? "ring-2" : ""}`}
      onClick={onClick}
    >
      {tag}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(tag); }}
          className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"
        >
          <FontAwesomeIcon icon={faXmark} className="w-2.5 h-2.5" />
        </button>
      )}
    </span>
  );
}

export function TagInput({ tags, onChange, suggestions }) {
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  const filteredSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    return suggestions.filter(
      (s) => s.toLowerCase().includes(q) && !tags.includes(s)
    );
  }, [input, suggestions, tags]);

  const addTag = (tag) => {
    const t = tag.trim();
    if (!t || tags.includes(t)) return;
    onChange([...tags, t]);
    setInput("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && input === "" && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const removeTag = (tag) => {
    onChange(tags.filter((t) => t !== tag));
  };

  return (
    <div className="relative">
      <div
        className="min-h-[42px] flex flex-wrap gap-1.5 items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-[#144677]/30 focus-within:border-[#144677] bg-white cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag) => (
          <TagChip key={tag} tag={tag} onRemove={removeTag} />
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          className="flex-1 min-w-[120px] text-sm outline-none bg-transparent placeholder-gray-400"
          placeholder={tags.length === 0 ? "Type a tag and press Enter..." : "Add another tag..."}
        />
      </div>

      {showSuggestions && (input.trim() || filteredSuggestions.length > 0) && (
        <div className="absolute z-10 mt-1 w-full rounded-lg bg-white shadow-lg ring-1 ring-gray-200 overflow-hidden">
          {input.trim() && !tags.includes(input.trim()) && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addTag(input)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
            >
              Add "<span className="font-medium">{input.trim()}</span>"
            </button>
          )}
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addTag(s)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <TagChip tag={s} size="xs" />
              <span className="text-gray-600">use existing</span>
            </button>
          ))}
          {filteredSuggestions.length === 0 && !input.trim() && (
            <div className="px-3 py-2 text-xs text-gray-400">No suggestions</div>
          )}
        </div>
      )}
    </div>
  );
}

export function TagFilterBar({ allTags, selectedTags, onToggle, onClear }) {
  if (allTags.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap px-5 pb-4">
      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex-shrink-0">
        <FontAwesomeIcon icon={faFilter} className="w-3 h-3" />
        Filter by tag
      </span>
      {allTags.map((tag) => {
        const active = selectedTags.includes(tag);
        return (
          <button
            key={tag}
            type="button"
            onClick={() => onToggle(tag)}
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-all ${tagPalette(tag)} ${
              active ? "opacity-100 shadow-sm scale-[1.04]" : "opacity-50 hover:opacity-80"
            }`}
          >
            {tag}
          </button>
        );
      })}
      {selectedTags.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 ml-1"
        >
          Clear filter
        </button>
      )}
    </div>
  );
}

export function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-md ring-1 ring-gray-200/50 p-4 flex items-center gap-3 hover:shadow-lg transition-shadow duration-200">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center flex-shrink-0`}>
        <FontAwesomeIcon icon={icon} className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
        <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{label}</p>
      </div>
    </div>
  );
}

export function KindBadge({ kind }) {
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.ADJUST;
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

export function ActiveBadge({ isActive }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
        isActive
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : "bg-red-50 text-red-600 ring-red-200"
      }`}
    >
      <FontAwesomeIcon icon={isActive ? faCircleCheck : faCircleXmark} className="w-3 h-3" />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

function formatCondEntry(key, value) {
  const [field, op] = (key || "").split("__");
  return {
    fieldLabel: FIELD_LABEL[field] || field || key,
    opLabel: OPERATOR_READABLE[op] || OPERATOR_LABEL[op] || op || "=",
    displayValue: value === true ? "Yes" : value === false ? "No" : String(value),
  };
}

export function ConditionsPopover({ conditions, conditionGroups }) {
  const [tooltipPos, setTooltipPos] = useState(null);
  const ref = useRef(null);

  const groups = Array.isArray(conditionGroups) && conditionGroups.some(
    (g) => Object.keys(g.conditions || {}).length > 0
  ) ? conditionGroups : null;

  const simpleEntries = Object.entries(conditions || {});
  const hasConditions = groups ? true : simpleEntries.length > 0;

  const handleMouseEnter = () => {
    if (!ref.current || !hasConditions) return;
    const rect = ref.current.getBoundingClientRect();
    setTooltipPos({
      x: Math.min(rect.left, window.innerWidth - 336),
      y: rect.bottom + 6,
    });
  };

  const cellContent = () => {
    if (!hasConditions) {
      return <span className="text-xs text-gray-400 italic">default (all consignments)</span>;
    }
    if (groups) {
      const total = groups.reduce((s, g) => s + Object.keys(g.conditions || {}).length, 0);
      return (
        <div className="flex items-center gap-1 flex-wrap">
          {groups.map((g, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[10px]">
              {i > 0 && (
                <span className={`font-bold px-1 py-0.5 rounded ${g.groupJoinOperator === "OR" ? "text-amber-700 bg-amber-50" : "text-blue-700 bg-blue-50"}`}>
                  {g.groupJoinOperator || "AND"}
                </span>
              )}
              <span className="bg-[#144677]/8 text-[#144677] rounded-md px-1.5 py-0.5 ring-1 ring-inset ring-[#144677]/15 font-medium">
                {Object.keys(g.conditions || {}).length} cond. ({g.conditionLogicOperator || "AND"})
              </span>
            </span>
          ))}
          <span className="text-[10px] text-gray-400 ml-0.5">{total} total</span>
        </div>
      );
    }
    const [firstKey, firstVal] = simpleEntries[0];
    const { fieldLabel, opLabel, displayValue } = formatCondEntry(firstKey, firstVal);
    const rest = simpleEntries.length - 1;
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="inline-flex items-center gap-1 rounded-md bg-[#144677]/8 px-2 py-0.5 text-[10px] text-[#144677] ring-1 ring-inset ring-[#144677]/15">
          <span className="font-semibold">{fieldLabel}</span>
          <span className="opacity-50 font-normal">{opLabel}</span>
          <span className="italic font-medium max-w-[72px] truncate">"{displayValue}"</span>
        </span>
        {rest > 0 && (
          <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 ring-1 ring-inset ring-gray-200">
            +{rest} more
          </span>
        )}
      </div>
    );
  };

  const tooltipBody = () => {
    const CondRow = ({ condKey, condVal }) => {
      const { fieldLabel, opLabel, displayValue } = formatCondEntry(condKey, condVal);
      return (
        <div className="flex items-baseline gap-1.5 py-1 border-b border-gray-50 last:border-0">
          <span className="text-gray-300 flex-shrink-0 text-[10px]">•</span>
          <span className="font-semibold text-gray-800 text-xs">{fieldLabel}</span>
          <span className="text-gray-400 text-[10px] flex-shrink-0">{opLabel}</span>
          <span className="text-[#144677] italic text-xs truncate max-w-[140px]">"{displayValue}"</span>
        </div>
      );
    };

    if (groups) {
      return groups.map((g, i) => {
        const entries = Object.entries(g.conditions || {});
        const logic = g.conditionLogicOperator || "AND";
        return (
          <div key={i}>
            {i > 0 && (
              <div className="flex items-center gap-2 my-2">
                <div className="flex-1 h-px bg-gray-100" />
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${g.groupJoinOperator === "OR" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                  {g.groupJoinOperator || "AND"} Group {i + 1}
                </span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
            )}
            {i === 0 && (
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Group 1
              </p>
            )}
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${logic === "OR" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
                Match {logic === "OR" ? "ANY" : "ALL"} of:
              </span>
            </div>
            <div className="pl-1">
              {entries.map(([k, v]) => <CondRow key={k} condKey={k} condVal={v} />)}
            </div>
          </div>
        );
      });
    }
    return (
      <>
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
          Match ALL of:
        </p>
        <div className="pl-1">
          {simpleEntries.map(([k, v]) => <CondRow key={k} condKey={k} condVal={v} />)}
        </div>
      </>
    );
  };

  return (
    <>
      <div
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setTooltipPos(null)}
        className="cursor-default"
      >
        {cellContent()}
      </div>
      {tooltipPos && createPortal(
        <div
          style={{ position: "fixed", left: tooltipPos.x, top: tooltipPos.y, zIndex: 9999 }}
          className="w-80 max-h-72 overflow-y-auto bg-white rounded-xl shadow-2xl ring-1 ring-gray-200 p-4"
          onMouseEnter={() => setTooltipPos(tooltipPos)}
          onMouseLeave={() => setTooltipPos(null)}
        >
          <p className="text-xs font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-100 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faFilter} className="w-3 h-3 text-gray-400" />
            Conditions
          </p>
          {tooltipBody()}
        </div>,
        document.body
      )}
    </>
  );
}

export function FilterInput({ value, onChange, placeholder }) {
  const active = String(value).trim() !== "";
  return (
    <div className="relative">
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        className={`absolute left-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 pointer-events-none transition-colors ${
          active ? "text-[#144677]" : "text-gray-300"
        }`}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full pl-6 pr-2 py-1.5 text-xs rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-[#144677]/20 focus:border-[#144677] ${
          active
            ? "border-[#144677] text-[#144677] font-medium bg-[#144677]/5"
            : "border-gray-200 text-gray-600 bg-white placeholder-gray-300"
        }`}
      />
    </div>
  );
}

export function ConditionRowBuilder({ rows, onChange, onAdd, onRemove }) {
  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const rowType = CONDITION_TYPE_BY_FIELD[row.field] || "text";
        const operatorOptions = OPERATOR_OPTIONS[rowType];
        const boolValue = String(row.value) === "true";
        return (
          <div key={row.id} className="flex flex-col sm:flex-row gap-2 p-2 rounded-lg bg-gray-50 ring-1 ring-gray-200">
            <select
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677] bg-white"
              value={row.field}
              onChange={(e) => onChange(row.id, "field", e.target.value)}
            >
              {CONDITION_FIELDS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
            <select
              className="w-full sm:w-36 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677] bg-white"
              value={row.operator}
              onChange={(e) => onChange(row.id, "operator", e.target.value)}
            >
              {operatorOptions.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
            {rowType === "bool" ? (
              <select
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677] bg-white"
                value={boolValue ? "true" : "false"}
                onChange={(e) => onChange(row.id, "value", e.target.value)}
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : (
              <input
                type={rowType === "date" ? "date" : rowType === "number" ? "number" : "text"}
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#144677]/30 focus:border-[#144677]"
                placeholder="Value"
                value={row.value}
                onChange={(e) => onChange(row.id, "value", e.target.value)}
              />
            )}
            <button
              type="button"
              className="flex-shrink-0 w-8 h-[38px] flex items-center justify-center border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
              onClick={() => onRemove(row.id)}
              disabled={rows.length === 1}
              title="Remove condition"
            >
              <FontAwesomeIcon icon={faXmark} className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={onAdd}
        className="flex items-center gap-1.5 text-xs font-medium text-[#144677] hover:text-[#11385f] transition-colors"
      >
        <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
        Add Condition
      </button>
    </div>
  );
}

export function DeleteButton({ onConfirm }) {
  const [confirming, setConfirming] = useState(false);
  if (confirming) {
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => { setConfirming(false); onConfirm(); }}
          className="px-2 py-1 text-xs font-semibold text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors"
        >
          Confirm
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className="w-7 h-7 flex items-center justify-center rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
      title="Delete rule"
    >
      <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
    </button>
  );
}

export function CompactConditionsText({ conditions }) {
  return <span>{conditionsToText(conditions)}</span>;
}

export { emptyConditionRow };
